import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CommonSearch from "../../../SharedFiles/CommonSearch/CommonSearch";
import QrContainer from "../../../SharedFiles/QRCode/qrReader";
import { convertCase } from "../../../SharedFiles/Helper/utils";
import { values } from "lodash";

const EXCLUDED_TEM_FIELDS = new Set([
  "docEntry",
  "baseEntry",
  "iWeight",
  "lineNum",
  "batch_Number",
  "quantity",
  "weight",
  "inVoiceDocEntry",
]);
const disabledFields = ["batch_Number"];
const hiddenFields = ["item_Code", "item_Name", "delivery_Number"];
const buildDocOption = (selectedItem) => {
  if (!selectedItem?.docEntries) return [];
  return JSON.parse(selectedItem.docEntries);
};
const buildModeSearchFields = (returnItems) => {
  if (!returnItems?.length) return [];
  return Object.keys(returnItems[0]).filter(
    (key) => !EXCLUDED_TEM_FIELDS.has(key)
  );
};

const ReturnModal = ({
  modelOpen,
  setModelOpen,
  openManual,
  setOpenManual,
  editMode,
  onAdd,
  deliveryNo,
  setDeliveryNo,
  handleValueChange,
  TABLE_HEADERS,
  setEditMode,
  formValues,
  setFormValues,
  handleSelectDelivery,
  handleSelectValue,
  initialValue,
}) => {
  const { t } = useTranslation();
  const { ReturnItems = [], InvoiceDetail = [] } = useSelector(
    (state) => state.ReturnData ?? {}
  );
  const [errors, setErrors] = React.useState({});
  const docOptions = useMemo(
    () => buildDocOption(initialValue),
    [initialValue]
  );
  const modelSearchInput = useMemo(
    () =>
      buildModeSearchFields(ReturnItems?.length ? ReturnItems : InvoiceDetail),
    [ReturnItems, InvoiceDetail]
  );

  const handleClose = () => {
    setEditMode(false);
    setFormValues({});
    setDeliveryNo({});
    setErrors({});
    setModelOpen(false);
  };
  const toggleMode = () => setOpenManual((prev) => !prev);
  const dialogTitle = useMemo(() => {
    if (!deliveryNo?.delivery_Number) return "Item Details";
    if (ReturnItems?.length) return "Return";
    if (InvoiceDetail?.length) return "Credit Memo";
    return "Item Details";
  }, [ReturnItems, InvoiceDetail, deliveryNo]);
  const fgReceiptTypeDrop = [
    { B: "BUNDLE" },
    { LB: "LOOSE BAR" },
    { WP: "WHITE PATCH" },
    { BP: "BLACK PATCH" },
    { NP: "NO PRINT" },
    { CD: "COATING DMG" },
    { DMG: "DAMAGE BAR" },
    { NWB: "NONWELDED BAR" },
  ];
console.log("deliveryNo",deliveryNo);

  // const uniqueReturnItems = useMemo(() => {
  //   if (!deliveryNo.delivery_Number) return [];
  //   if (!ReturnItems?.length && !InvoiceDetail?.length) return [];
  //   const seen = new Set();
  //   return ReturnItems?.length ? ReturnItems : InvoiceDetail;
  // }, [ReturnItems, InvoiceDetail, deliveryNo.delivery_Number]);

  const uniqueReturnItems = useMemo(() => {
    if (!deliveryNo.delivery_Number) return [];
    if (!ReturnItems?.length && !InvoiceDetail?.length) return [];

    // If ReturnItems exists → return directly
    if (ReturnItems?.length) return ReturnItems;

    // Else dedupe InvoiceDetail
    const map = new Map();

    (InvoiceDetail || []).forEach((item) => {
      const key = `${item?.item_Code}-${item?.lineNum}`;

      if (!map.has(key)) {
        map.set(key, item);
      }
    });

    return Array.from(map.values());
  }, [ReturnItems, InvoiceDetail, deliveryNo.delivery_Number]);
  const isInvoiceDetailMode = !ReturnItems?.length && !!InvoiceDetail?.length;
  const batchOptions = useMemo(() => {
    if (!isInvoiceDetailMode) return [];
    if (!formValues.item_Code && !formValues.item_Name) return [];
    return InvoiceDetail.filter(
      (row) =>
        row.item_Code === formValues.item_Code ||
        row.item_Name === formValues.item_Name
    )
      .map((row) => row.batch_Number)
      .filter(Boolean)
      .map((b) => ({ label: b, value: b }));
  }, [
    InvoiceDetail,
    formValues.item_Code,
    formValues.item_Name,
    isInvoiceDetailMode,
  ]);

  const receiptTypeOptions = fgReceiptTypeDrop.map((item) => {
    const key = Object.keys(item)[0];

    return { label: item[key], value: key };
  });
  // console.log("formValues",formValues);

  const REQUIRED_FIELDS = [
    "Quantity",
    "Receipt_Type",
    "Weight",
    "batch_Number",
    "item_Code",
    "item_Name",
  ];
  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = isInvoiceDetailMode
      ? REQUIRED_FIELDS.filter((field) => field !== "Receipt_Type")
      : REQUIRED_FIELDS;

    fieldsToValidate.forEach((field) => {
      if (!formValues[field] || formValues[field] === "") {
        newErrors[field] = `${convertCase(field)} is required`;
      }
    });

    // Special validation for delivery_No
    if (!deliveryNo.delivery_Number) {
      newErrors.delivery_Number = "delivery_No is required";
    }
    // console.log("newErrors",newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    const submissionData = { ...formValues, ...deliveryNo };
    // Convert Receipt_Type to label only
    if (submissionData.Receipt_Type?.label) {
      submissionData.Receipt_Type = submissionData.Receipt_Type.label;
    }
    onAdd(submissionData);
    // onAdd(formValues);
    setFormValues({});
    setDeliveryNo({});
    setErrors({});
    setModelOpen(false);
  };

  return (
    <Dialog open={modelOpen}>
      <DialogTitle className="Modal-heading">  {t(dialogTitle)}</DialogTitle>
      <DialogContent className="custom-dialog-content">
        {openManual ? (
          <div>
            <div style={{ marginBottom: "16px" }}>
              <CommonSearch
                inputFields={["delivery_Number"]}
                data={docOptions}
                values={deliveryNo}
                setValues={setDeliveryNo}
                handleDropdownClick={handleSelectDelivery}
                errorMessage={errors}
              />
              {/* ))} */}
            </div>
            <div className="d-grid">
              <CommonSearch
                inputFields={modelSearchInput}
                data={uniqueReturnItems}
                values={formValues}
                setValues={setFormValues}
                handleDropdownClick={handleSelectValue}
                errorMessage={errors}
              />
              {(formValues.item_Code || formValues.item_Name) &&
                TABLE_HEADERS.filter(
                  (fieldName) => !hiddenFields.includes(fieldName)
                )
                  .filter((fieldName) => {
                    if (isInvoiceDetailMode && fieldName === "Receipt_Type") {
                      return false;
                    }
                    return true;
                  })
                  .map((fieldName) => {
                    // console.log("fieldName",fieldName);

                    const isReceiptType = fieldName === "Receipt_Type";
                    const isBatchSelect =
                      fieldName === "batch_Number" && isInvoiceDetailMode;
                    const isSelectField = isReceiptType || isBatchSelect;
                    const isQuantityField = fieldName === "Quantity";
                    const isDisabled = disabledFields.includes(fieldName);
                    const label = fieldName.replace(/_/g, " ");
                    const fieldOptions = isReceiptType
                      ? receiptTypeOptions
                      : batchOptions;
                    return (
                      <div
                        key={fieldName}
                        style={{ marginTop: 16, width: "100%" }}
                      >
                        {isSelectField ? (
                          <Autocomplete
                            options={fieldOptions}
                            getOptionLabel={(option) => option?.label || ""}
                            value={
                              fieldOptions.find(
                                (option) =>
                                  option.value ===
                                    formValues[fieldName]?.value ||
                                  option.label === formValues[fieldName]
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleSelectValue(newValue, fieldName);
                            }}
                            filterSelectedOptions
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label={label}
                                placeholder={`Select ${label}`}
                                required={REQUIRED_FIELDS.includes(fieldName)}
                                error={!!errors[fieldName]}
                                helperText={errors[fieldName] || ""}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            value={formValues[fieldName] || ""}
                            // onChange={(e) => handleValueChange(e.target.value,fieldName)}
                            onChange={(e) => {
                              let inputValue = e.target.value;
                              if (isQuantityField) {
                                inputValue = inputValue
                                  .replace(/[^0-9.]/g, "") // Remove everything except digits and dot
                                  .replace(/(\..*)\./g, "$1");
                              }
                              handleValueChange(inputValue, fieldName);
                            }}
                            variant="outlined"
                            label={label}
                            disabled={isDisabled}
                            placeholder={`Enter ${label}`}
                            style={{ width: "100%" }}
                            required={REQUIRED_FIELDS.includes(fieldName)}
                            error={!!errors[fieldName]}
                            helperText={errors[fieldName] || ""}
                          />
                        )}
                      </div>
                    );
                  })}
            </div>
          </div>
        ) : (
          <div>
            <QrContainer elementId="Delivery_Screen" openManual={openManual} />
          </div>
        )}
      </DialogContent>
      <DialogActions className="custom-dialog-actions">
        <Button className="Modal-cancel" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="Modal-submit" onClick={handleAdd}>
          {editMode ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnModal;

// import React, { useMemo, useCallback } from "react";
// import { useSelector } from "react-redux";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   TextField,
//   Autocomplete,
// } from "@mui/material";
// import { useTranslation } from "react-i18next";
// import CommonSearch from "../../../SharedFiles/CommonSearch/CommonSearch";
// import QrContainer from "../../../SharedFiles/QRCode/qrReader";
// import { convertCase } from "../../../SharedFiles/Helper/utils";

// // Constants defined outside component to avoid recreation on every render
// const EXCLUDED_TEM_FIELDS = new Set([
//   "docEntry",
//   "baseEntry",
//   "iWeight",
//   "lineNum",
//   "batch_Number",
//   "quantity",
//   "u_QTYINNOS",
// ]);

// const disabledFields = ["batch_Number"];
// const hiddenFields = ["item_Code", "item_Name", "delivery_Number"];
// const REQUIRED_FIELDS = [
//   "Quantity",
//   "Receipt_Type",
//   "Weight",
//   "batch_Number",
//   "item_Code",
//   "item_Name",
// ];

// const fgReceiptTypeDrop = [
//   { B: "BUNDLE" },
//   { LB: "LOOSE BAR" },
//   { WP: "WHITE PATCH" },
//   { BP: "BLACK PATCH" },
//   { NP: "NO PRINT" },
//   { CD: "COATING DMG" },
//   { DMG: "DAMAGE BAR" },
//   { NWB: "NONWELDED BAR" },
// ];

// const receiptTypeOptions = fgReceiptTypeDrop.map((item) => {
//   const key = Object.keys(item)[0];
//   return { label: item[key], value: key };
// });

// const buildDocOption = (selectedItem) => {
//   if (!selectedItem?.docEntries) return [];
//   return JSON.parse(selectedItem.docEntries);
// };

// const buildModeSearchFields = (returnItems) => {
//   if (!returnItems?.length) return [];
//   return Object.keys(returnItems[0]).filter((key) => !EXCLUDED_TEM_FIELDS.has(key));
// };

// const ReturnModal = ({
//   modelOpen,
//   setModelOpen,
//   openManual,
//   setOpenManual,
//   editMode,
//   onAdd,
//   deliveryNo,
//   setDeliveryNo,
//   handleValueChange,
//   TABLE_HEADERS,
//   setEditMode,
//   formValues,
//   setFormValues,
//   handleSelectDelivery,
//   handleSelectValue,
//   initialValue,
// }) => {
//   const { t } = useTranslation();
//   const { ReturnItems = [], InvoiceDetail = [] } = useSelector(
//     (state) => state.ReturnData ?? {}
//   );

//   const [errors, setErrors] = React.useState({});

//   const docOptions = useMemo(
//     () => buildDocOption(initialValue),
//     [initialValue]
//   );

//   const modelSearchInput = useMemo(
//     () => buildModeSearchFields(ReturnItems?.length ? ReturnItems : InvoiceDetail),
//     [ReturnItems, InvoiceDetail]
//   );

//   const handleClose = useCallback(() => {
//     setEditMode(false);
//     setFormValues({});
//     setDeliveryNo({});
//     setErrors({});
//     setModelOpen(false);
//   }, [setEditMode, setFormValues, setDeliveryNo, setErrors, setModelOpen]);

//   const toggleMode = useCallback(() =>
//     setOpenManual((prev) => !prev),
//     [setOpenManual]
//   );

//   const isInvoiceDetailMode = !ReturnItems?.length && !!InvoiceDetail?.length;

//   const sourceData = useMemo(
//     () => (ReturnItems?.length ? ReturnItems : InvoiceDetail),
//     [ReturnItems, InvoiceDetail]
//   );

//   const uniqueReturnItems = useMemo(() => {
//     if (!deliveryNo.delivery_Number || !sourceData?.length) return [];
//     return sourceData;
//   }, [sourceData, deliveryNo.delivery_Number]);

//   const batchOptions = useMemo(() => {
//     if (!isInvoiceDetailMode || !formValues.item_Code && !formValues.item_Name) return [];

//     const filtered = InvoiceDetail.filter(
//       (row) =>
//         row.item_Code === formValues.item_Code ||
//         row.item_Name === formValues.item_Name
//     );

//     const batchNumbers = filtered
//       .map((row) => row.batch_Number)
//       .filter(Boolean);

//     // Use Set for deduplication instead of multiple array operations
//     const uniqueBatches = [...new Set(batchNumbers)];

//     return uniqueBatches.map((b) => ({ label: b, value: b }));
//   }, [InvoiceDetail, formValues.item_Code, formValues.item_Name, isInvoiceDetailMode]);

//   const visibleTableHeaders = useMemo(
//     () => TABLE_HEADERS.filter((fieldName) => !hiddenFields.includes(fieldName)),
//     [TABLE_HEADERS]
//   );

//   const validateForm = useCallback(() => {
//     const newErrors = {};

//     // Only validate fields that have values or are missing
//     for (const field of REQUIRED_FIELDS) {
//       if (!formValues[field] || formValues[field] === "") {
//         newErrors[field] = `${convertCase(field)} is required`;
//       }
//     }

//     if (!deliveryNo.delivery_Number) {
//       newErrors.delivery_Number = "delivery_No is required";
//     }
//     setErrors(newErrors);
//     return Object.keys(newErrors)?.length === 0;
//   }, [formValues, deliveryNo]);

//   const handleAdd = useCallback(() => {
//     if (!validateForm()) return;

//     const submissionData = { ...formValues, ...deliveryNo };

//     // Convert Receipt_Type to label only
//     if (submissionData.Receipt_Type?.label) {
//       submissionData.Receipt_Type = submissionData.Receipt_Type.label;
//     }

//     onAdd(submissionData);
//     setFormValues({});
//     setDeliveryNo({});
//     setErrors({});
//     setModelOpen(false);
//   }, [validateForm, formValues, deliveryNo, onAdd, setFormValues, setDeliveryNo, setErrors, setModelOpen]);

//   const handleFieldChange = useCallback((e, fieldName) => {
//     let inputValue = e.target.value;

//     if (fieldName === "Quantity") {
//       inputValue = inputValue
//         .replace(/[^0-9.]/g, "")
//         .replace(/(\..*)\./g, "$1");
//     }

//     handleValueChange(inputValue, fieldName);
//   }, [handleValueChange]);

//   const renderField = useCallback((fieldName) => {
//     const isReceiptType = fieldName === "Receipt_Type";
//     const isBatchSelect = fieldName === "batch_Number" && isInvoiceDetailMode;
//     const isSelectField = isReceiptType || isBatchSelect;
//     const isDisabled = disabledFields.includes(fieldName);
//     const label = fieldName.replace(/_/g, " ");
//     const fieldOptions = isReceiptType ? receiptTypeOptions : batchOptions;
//     const currentValue = formValues[fieldName];

//     const selectedOption = isSelectField
//       ? fieldOptions.find(
//           (option) =>
//             option.value === currentValue?.value || option.label === currentValue
//         ) || null
//       : null;

//     return (
//       <div key={fieldName} style={{ marginTop: 16, width: "100%" }}>
//         {isSelectField ? (
//           <Autocomplete
//             options={fieldOptions}
//             getOptionLabel={(option) => option?.label || ""}
//             value={selectedOption}
//             onChange={(event, newValue) => {
//               console.log("newValue",newValue);

//               handleSelectValue(newValue, fieldName);
//             }}
//             filterSelectedOptions
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 variant="outlined"
//                 label={label}
//                 placeholder={`Select ${label}`}
//                 required={REQUIRED_FIELDS.includes(fieldName)}
//                 error={!!errors[fieldName]}
//                 helperText={errors[fieldName] || ""}
//               />
//             )}
//           />
//         ) : (
//           <TextField
//             value={currentValue || ""}
//             onChange={(e) => handleFieldChange(e, fieldName)}
//             variant="outlined"
//             label={label}
//             disabled={isDisabled}
//             placeholder={`Enter ${label}`}
//             style={{ width: "100%" }}
//             required={REQUIRED_FIELDS.includes(fieldName)}
//             error={!!errors[fieldName]}
//             helperText={errors[fieldName] || ""}
//           />
//         )}
//       </div>
//     );
//   }, [formValues, errors, batchOptions, isInvoiceDetailMode, handleSelectValue, handleFieldChange]);

//   return (
//     <Dialog open={modelOpen}>
//       <DialogTitle className="Modal-heading">{t("Item Details")}</DialogTitle>
//       <DialogContent className="custom-dialog-content">
//         {openManual ? (
//           <div>
//             <div style={{ marginBottom: "16px" }}>
//               <CommonSearch
//                 inputFields={["delivery_Number"]}
//                 data={docOptions}
//                 values={deliveryNo}
//                 setValues={setDeliveryNo}
//                 handleDropdownClick={handleSelectDelivery}
//                 errorMessage={errors}
//               />
//             </div>
//             <div className="d-grid">
//               <CommonSearch
//                 inputFields={modelSearchInput}
//                 data={uniqueReturnItems}
//                 values={formValues}
//                 setValues={setFormValues}
//                 handleDropdownClick={handleSelectValue}
//                 errorMessage={errors}
//               />
//               {visibleTableHeaders.map(renderField)}
//             </div>
//           </div>
//         ) : (
//           <div>
//             <QrContainer elementId="Delivery_Screen" openManual={openManual} />
//           </div>
//         )}
//       </DialogContent>
//       <DialogActions className="custom-dialog-actions">
//         <Button className="Modal-cancel" onClick={handleClose}>
//           Cancel
//         </Button>
//         <Button className="Modal-submit" onClick={handleAdd}>
//           {editMode ? "Update" : "Add"}
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default ReturnModal;
